import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  ChevronRight as ChevronIcon,
  Assessment as ReportsIcon,
} from '@mui/icons-material';
import { Incident } from '../Types/Incident';
import { getIncidents } from '../Services/IncidentService';
import IncidentIcon from '../Components/Incidents/IncidentIcon';
import IncidentStatusChip from '../Components/Incidents/IncidentStatusChip';
import { appColors } from '../Config/Theme';
import { formatDateTime, parseUtc } from '../Utils/dateHelpers';
import { isIncidentClosed } from '../Utils/incidentHelpers';

type StatusFilter = 'All' | 'Closed' | 'Active';

const formatDuration = (startUtc: string, endUtc: string | null): string => {
  if (!endUtc) return 'Ongoing';
  try {
    const diff = parseUtc(endUtc).getTime() - parseUtc(startUtc).getTime();
    if (diff <= 0) return '—';
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    if (hrs < 24) return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
    const days = Math.floor(hrs / 24);
    const remHrs = hrs % 24;
    return remHrs > 0 ? `${days}d ${remHrs}h` : `${days}d`;
  } catch {
    return '—';
  }
};

const StatCell: React.FC<{ label: string; value: number | string; color: string; emphasize?: boolean }> = ({
  label, value, color, emphasize,
}) => (
  <Box sx={{ textAlign: 'center', minWidth: 56 }}>
    <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, color, lineHeight: 1.2, ...(emphasize ? { fontSize: '1rem' } : {}) }}
    >
      {value}
    </Typography>
  </Box>
);

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getIncidents();
      // Normalize: handle data as array, or { data: [...] }, etc.
      let list: Incident[] = [];
      if (Array.isArray(response.data)) {
        list = response.data;
      } else if (response.data && typeof response.data === 'object') {
        const obj = response.data as Record<string, unknown>;
        if (Array.isArray(obj.items)) list = obj.items as Incident[];
      }
      setIncidents(list);

      // Default to Closed if any closed incidents exist
      const hasClosed = list.some((i) => isIncidentClosed(i));
      setStatusFilter(hasClosed ? 'Closed' : 'All');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incidents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredIncidents = useMemo(() => {
    let result = incidents;

    // Status filter
    if (statusFilter === 'Active') {
      result = result.filter((i) => !isIncidentClosed(i));
    } else if (statusFilter === 'Closed') {
      result = result.filter((i) => isIncidentClosed(i));
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((i) =>
        i.incidentName?.toLowerCase().includes(q) ||
        i.location?.toLowerCase().includes(q) ||
        (isIncidentClosed(i) ? 'closed' : 'active').includes(q),
      );
    }

    // Sort: most recent first
    result = [...result].sort((a, b) => {
      const aTime = a.closedAtUtc || a.startedAtUtc || '';
      const bTime = b.closedAtUtc || b.startedAtUtc || '';
      return bTime.localeCompare(aTime);
    });

    return result;
  }, [incidents, statusFilter, searchQuery]);

  const filterCounts = useMemo(() => ({
    all: incidents.length,
    active: incidents.filter((i) => !isIncidentClosed(i)).length,
    closed: incidents.filter((i) => isIncidentClosed(i)).length,
  }), [incidents]);

  return (
    <Box sx={{ pt: 1, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto', width: '100%' }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: appColors.text.heading }}>
            Incident Reports
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.body, mt: 0.5, maxWidth: 600 }}>
            Review closed incident summaries, accountability outcomes, and audit history.
          </Typography>
        </Box>
      </Box>

      {/* ── Search + Filters ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by name, location, or status..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: appColors.text.placeholder }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: 1, maxWidth: 500 }}
        />
      </Box>

      {/* ── Status chips ── */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {([
          { label: 'Closed', value: 'Closed' as StatusFilter, count: filterCounts.closed },
          { label: 'Active', value: 'Active' as StatusFilter, count: filterCounts.active },
          { label: 'All', value: 'All' as StatusFilter, count: filterCounts.all },
        ]).map((chip) => {
          const isActive = statusFilter === chip.value;
          return (
            <Chip
              key={chip.value}
              label={`${chip.label} (${chip.count})`}
              onClick={() => setStatusFilter(chip.value)}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: '20px',
                ...(isActive
                  ? { bgcolor: appColors.primary.main, color: '#fff', '&:hover': { bgcolor: appColors.primary.dark } }
                  : { borderColor: appColors.border.default, color: appColors.text.body }),
              }}
            />
          );
        })}
      </Box>

      {/* ── Loading ── */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* ── Error ── */}
      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      {/* ── Empty state ── */}
      {!loading && !error && filteredIncidents.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            bgcolor: appColors.surface.card,
            borderRadius: '12px',
            border: `1px solid ${appColors.border.default}`,
          }}
        >
          <ReportsIcon sx={{ fontSize: 48, color: appColors.text.placeholder, mb: 1 }} />
          <Typography variant="h6" sx={{ color: appColors.text.caption, fontWeight: 600 }}>
            {incidents.length === 0 ? 'No incidents found' : 'No incidents match your filters'}
          </Typography>
          <Typography variant="body2" sx={{ color: appColors.text.placeholder, mt: 0.5 }}>
            {incidents.length === 0
              ? 'Incident reports will appear here once incidents are created.'
              : 'Try adjusting your search or filter criteria.'}
          </Typography>
        </Box>
      )}

      {/* ── Incident list ── */}
      {!loading && !error && filteredIncidents.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filteredIncidents.map((incident) => {
            const isActive = !isIncidentClosed(incident);
            const pct = Math.round(incident.currentCounts?.percentComplete ?? 0);
            const counts = incident.currentCounts ?? { total: 0, accounted: 0, notAccounted: 0, notChecked: 0, tenants: 0, percentComplete: 0 };
            const dateLabel = isActive
              ? `Started ${formatDateTime(incident.startedAtUtc)}`
              : `Ended ${incident.closedAtUtc ? formatDateTime(incident.closedAtUtc) : 'N/A'}`;

            return (
              <Box
                key={incident.incidentId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: appColors.surface.card,
                  borderRadius: '12px',
                  px: { xs: 1.5, sm: 2.5 },
                  py: 2,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.15s',
                  '&:hover': { boxShadow: appColors.shadow.rowHover },
                  gap: { xs: 1.5, sm: 2 },
                }}
                onClick={() => navigate(`/incidents/${incident.incidentId}/report`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/incidents/${incident.incidentId}/report`); }}
              >
                {/* Icon */}
                <IncidentIcon incidentType={incident.incidentType} />

                {/* Info */}
                <Box sx={{ flex: '1 1 0', minWidth: 0, mr: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, color: appColors.text.heading }}>
                    {incident.incidentName || incident.location || 'Unnamed Incident'}
                  </Typography>
                  {incident.location && incident.incidentName && (
                    <Typography variant="body2" sx={{ color: appColors.text.body, lineHeight: 1.4 }}>
                      {incident.location}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 14, color: appColors.text.placeholder }} />
                      <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>
                        {dateLabel}
                      </Typography>
                    </Box>
                    {!isActive && incident.closedAtUtc && (
                      <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                        Duration: {formatDuration(incident.startedAtUtc, incident.closedAtUtc)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Progress */}
                <Box sx={{ width: 72, minWidth: 72, display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 500 }}>
                    Accounted
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: pct === 100 ? appColors.status.success : appColors.primary.main, lineHeight: 1.3 }}
                  >
                    {pct}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: appColors.border.default,
                      '& .MuiLinearProgress-bar': { bgcolor: appColors.status.successLight, borderRadius: 3 },
                    }}
                  />
                </Box>

                {/* Stats */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, ml: 2 }}>
                  <StatCell label="Total" value={counts.total} color={appColors.primary.main} />
                  <StatCell label="Accounted" value={counts.accounted} color={appColors.status.success} />
                  <StatCell
                    label="Not Accounted"
                    value={counts.notAccounted}
                    color={appColors.status.error}
                    emphasize={counts.notAccounted > 0}
                  />
                </Box>

                {/* Status + action */}
                <Box sx={{ ml: { xs: 'auto', md: 2 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IncidentStatusChip status={incident.status} />
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<ChevronIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/incidents/${incident.incidentId}/report`);
                    }}
                    sx={{ display: { xs: 'none', sm: 'flex' }, whiteSpace: 'nowrap' }}
                  >
                    View Report
                  </Button>
                  <ChevronIcon sx={{ color: appColors.border.hover, fontSize: 24, display: { xs: 'block', sm: 'none' } }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ReportsPage;
